/**
 * exercise-container.js
 */

import React from 'react';
import { useMediaQuery, Tabs, Tab } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import {
  useParams,
  Switch,
} from "react-router-dom";
import { Types } from 'utils/constants';
import { paths } from 'utils/routing';
import { UserContext } from 'contexts/user/user-context';
import useRecordStore from 'contexts/record-store';
import RouteLink from 'routing/route-link';
import Route from 'routing/route';
import DrawerContentBuilder from 'components/menu-drawer/drawer-content-builder';
import AnswerMenuItem from 'components/training/answer/menu-item';
import TrainingWizardComplete from 'components/training/training-drawer/training-wizard-complete';
import { DetectionOdorsProvider } from 'components/detection-odors/context/detection-odors-context';
import ExerciseView from '../view';
import ExerciseEdit from '../edit';
import AnswerView from '../../answer/view';
import AnswerEdit from '../../answer/edit';


const useStyles = makeStyles(theme => ({
  tabsContainer: {
    padding: `${theme.spacing(2)} ${theme.spacing(1)} ${theme.spacing(4)} ${theme.spacing(1)}`,
    marginTop: theme.spacing(-2),
  },
  stickyHeaderContainer: {
    display: 'flex', 
    justifyContent: 'space-between',
    padding: `${theme.spacing(1)} ${theme.spacing(3)}`,
  },
  tabs: {
    '& .MuiTab-root': {
      minWidth: 'auto',
      borderBottom: `1px solid ${theme.palette.grey.colors[7]}`,
      textTransform: 'none',
      padding: `6px ${theme.spacing(3)}`,
      '&:hover': {
        transition: `${theme.transitions.duration.standard}ms ${theme.transitions.easing.easeInOut}`,
        backgroundColor: theme.palette.sys.surfaceContainerHigh,
      }
    },
    '& .Mui-selected': {
      backgroundColor: theme.palette.sys.surfaceContainerHigh,
    },
    '& .MuiTab-wrapper': {
      flexDirection: 'row',
      '& > .MuiSvgIcon-root': {
        marginRight: theme.spacing(1)
      }
    }
  },
}));


export default function ExerciseContainer(props) {
  const {
    wizard,
    basePath,
    drawerState,
  } = props;
  const classes = useStyles();
  const { user } = React.useContext(UserContext);
  const isMobileDrawer = useMediaQuery(theme => theme.breakpoints.down('sm_md'), { noSsr: true });
  const recordStore = useRecordStore();
  const params = useParams();
  const exerciseId = parseInt(params.exerciseId) || 0;
  const answerId = parseInt(params.answerId) || 0;
  const exerciseRecord = recordStore.useRecord(Types.Record.EXERCISE, exerciseId);
  const eventRecord = recordStore.useRecord(Types.Record.EVENT, exerciseRecord.get()?.eventId);
  const hasExercise = Boolean(exerciseRecord.get());
  const [answers, setAnswers] = React.useState();
  const [tabIndex, setTabIndex] = React.useState();
  const [desktopActions, setDesktopActions] = React.useState();
  const [mobileAppBar, setMobileAppBar] = React.useState();
  const drawerBasePath = basePath + paths.training.base;
  const routeProps = {
    ...props,
    setDesktopActions,
    setMobileAppBar
  };


  // answer list will be full objects if exer is being created instead of answer ids.
  // this is because we don't have real answer ids yet but need dog names.
  const exerciseAnswerList = exerciseRecord.get() && exerciseRecord.get().answers.length > 0
    ? typeof exerciseRecord.get().answers[0] === 'string'
      ? exerciseRecord.get().answers.join('-')
      : exerciseRecord.get().answers.map(a => a.id).join('-')
    : '';


  // determine answers and tab index
  React.useEffect(() => {
    if (hasExercise) {
      const newAnswers = exerciseRecord.get()?.answers;
      let newTabIndex = null;
      if (exerciseId === 0 || !answerId) {
        newTabIndex = 0;
      } else if (newAnswers.includes(answerId)) {
        newTabIndex = newAnswers.indexOf(answerId) + 1;
      } else {
        drawerState.loading.content.set(false);
        newTabIndex = -1;
      }

      setAnswers(newAnswers);
      setTabIndex(newTabIndex);
    } else if (exerciseId === 0) {
      setTabIndex(0);
    }

  // exerciseRecord can't be in
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exerciseId, exerciseAnswerList, answerId, hasExercise]);


  const tabs = tabIndex >= 0 && answers && (
    <Tabs
      indicatorColor="primary"
      textColor="primary"
      variant="scrollable"
      scrollButtons
      aria-label={`exercise-tabs-${exerciseId}`}
      data-testid="exercise-tablist"
      className={`${classes.tabs} scrollable-tabs`}
      value={tabIndex}
      allowScrollButtonsMobile>
      <Tab
        component={RouteLink}
        label='Details'
        basePath={basePath}
        pathSectionName='training'
        pathItemName='viewExercise'
        pathParams={{exerciseId}}
        disabled={exerciseId === 0}
        data-testid='exercise-view-tab'
      />
      {answers && answers.map((answer, i) => {
        const isObj = typeof answer === 'object' && answer !== null;
        return <AnswerMenuItem
          key={i}
          id={isObj ? answer.id : answer}
          answer={isObj ? answer : null}
          type='tab'
          basePath={basePath}
          membership={eventRecord.get()?.membership}
          showK9Prefix={answers.length < 3}
        />
      })}
    </Tabs>
  );


  const stickyHeader = isMobileDrawer
    ? null
    : user.role.isHandler
      ? {
        offset: -30,
        content: (
          <div className={classes.stickyHeaderContainer}>
            <span className='flex-hc' style={{ minWidth: 0 }}>
              {tabs}
            </span>
            <span className='flex-hc'>
              {desktopActions}
            </span>
          </div>
        )
      }
      : true;


  return (
    <>
      <DetectionOdorsProvider>
        {tabIndex !== undefined && 
        <DrawerContentBuilder
          mobileAppBar={mobileAppBar}
          desktopHeader={{ 
            title: tabIndex >= 0
              ? exerciseRecord.get() && exerciseRecord.get().name
              : `Answer ${answerId} not found for exercise ${exerciseId}`,
            actions: desktopActions
          }}
          stickyHeader={stickyHeader}
          children={(triggerElement, desktopHeader) => (
            <>
              <TrainingWizardComplete basePath={basePath} />
              {desktopHeader}
              {triggerElement}

              {/* details & answer tabs - handler & desktop-only */}
              {(user.role.isHandler && !isMobileDrawer) &&
              <div className={classes.tabsContainer}>
                {tabs}
              </div>}

              {/* detail & answer routing */}
              {tabIndex >= 0 &&
              <Switch>
                {wizard && wizard.getExerciseContainerRoutes(basePath).map((route, i) => {
                  return <Route
                    key={i}
                    path={route.path}
                    render={() => <route.component {...routeProps} />}
                  />;
                })}
                <Route
                  path={drawerBasePath + paths.training.editAnswer.path}
                  children={<AnswerEdit {...routeProps} />}
                />
                <Route
                  path={drawerBasePath + paths.training.viewAnswer.path}
                  children={<AnswerView {...routeProps} />}
                />
                <Route
                  path={[
                    drawerBasePath + paths.training.editExercise.path,
                    drawerBasePath + paths.training.addExercise.path,
                  ]}
                  children={<ExerciseEdit {...routeProps} />}
                />
                <Route
                  path={drawerBasePath + paths.training.viewExercise.path}
                  children={<ExerciseView {...routeProps} />}
                />
              </Switch>}
            </>
          )}
        />}
      </DetectionOdorsProvider>
    </>
  );
}